import * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { getMedia } from "../../api/storages";
import { UserContext } from '../../functions/Auth';
import { convertBytesToKB } from '../../utils';

// component
import DescriptionInsertMedia from "../Media/DescriptionInsertMedia";
import MediaCard from './MediaCard';
import StorageUsageBar from './StorageUsageBar';

// mui
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// types
type MediaType = {
    id: number,
    url: string,
    name: string,
    size: number,
    uploadedDate: string,
}

// type
type MediaListProps = {
    redrawByUpload: Date | undefined,
    redrawByDelete: number | undefined,
    storageUsed: number,
    storageTotal: number,
    setStorageUsed: React.Dispatch<React.SetStateAction<number>>,
    handleDeleteMedia: (mediaId: number) => void,
}

export default function MediaList(props: MediaListProps) {
    const { redrawByUpload, redrawByDelete, storageUsed, storageTotal, setStorageUsed, handleDeleteMedia } = props;
    const userInfo = useContext(UserContext);
    const userId = userInfo?.userId;

    /**
     *  ソート
     */
    const [sort, setSort] = useState('');

    const fnChangeSort = useCallback((mediaList: MediaType[], sort: string) => {
        if (sort === "date-desc") {
            sortDateDesc(mediaList);
        } else if (sort === "date-asc") {
            sortDateAsc(mediaList);
        } else {
            setMediaList(mediaList);
        }
    }, []);

    const handleChangeSort = (event: SelectChangeEvent, mediaList: MediaType[]) => {
        fnChangeSort(mediaList, event.target.value);
        setSort(event.target.value);
    }

    // 更新日（新しい順）
    const sortDateDesc = (mediaList: MediaType[]) => {
        const tempMediaList = [...mediaList].sort((a, b) => {
            const aDateMillis = new Date(a.uploadedDate).getTime();
            const bDateMillis = new Date(b.uploadedDate).getTime();
            return -(aDateMillis - bDateMillis);

        });
        setMediaList(tempMediaList);
    }

    // 更新日（古い順）
    const sortDateAsc = (mediaList: MediaType[]) => {
        const tempMediaList = [...mediaList].sort((a, b) => {
            const aDateMillis = new Date(a.uploadedDate).getTime();
            const bDateMillis = new Date(b.uploadedDate).getTime();
            return aDateMillis - bDateMillis;
        });
        setMediaList(tempMediaList);
    }

    // ページネーション
    const [page, setPage] = useState<number>(1); // ページ番号
    const [pageCount, setPageCount] = useState<number>(1); // ページ数
    const [displayedMediaList, setdisplayedMediaList] = useState<MediaType[]>([]); // 表示データ
    const displayNum = 10; //1ページあたりの項目数

    /**
    * メディアの取得
    */
    const [mediaList, setMediaList] = useState<MediaType[]>([]); // トピックリスト

    const fnGetMedia = useCallback(async (userId: string) => {
        const response = await getMedia(userId);
        if (response) {
            setMediaList(response.reverse());
            setStorageUsed(response.reduce((acc: any, media: { size: any; }) => acc + media.size, 0));
        }
    }, [setStorageUsed]);

    // ファイル状態の再描画
    useEffect(() => {
        if (!userId) {
            return;
        }

        fnGetMedia(userId.toString());
    }, [userId, redrawByUpload, redrawByDelete, fnGetMedia]);

    useEffect(() => {
        if (mediaList) {
            //ページカウントの計算（今回は3項目/ページなので4ページ）
            setPageCount(Math.ceil(mediaList.length / displayNum));
            //表示データを抽出
            setdisplayedMediaList(mediaList.slice(((page - 1) * displayNum), page * displayNum))
        }
    }, [mediaList, page]);

    const handleChangePagination = (event: React.ChangeEvent<unknown>, page: number) => {
        //ページ移動時にページ番号を更新
        setPage(page);
        //ページ移動時に表示データを書き換える
        setdisplayedMediaList(mediaList.slice(((page - 1) * displayNum), page * displayNum))
    }

    return (
        <>
            <Box
                sx={{
                    maxWidth: 980,
                    mx: "auto",
                    pt: 3,
                    px: 3,
                }}
            >
                <Typography
                    variant="h5"
                    component="h1"
                    fontWeight="700"
                    gutterBottom
                >アップロードファイル</Typography>
                <Typography paragraph>URLをコピーしてトピックやコメントに張り付けてください。</Typography>

                {/*ストレージ使用状態*/}
                <Box sx={{ mb: 2 }}>
                    <div style={{ width: '40%' }}>
                        <StorageUsageBar
                            used={storageUsed}
                            total={storageTotal}
                        />
                    </div>
                </Box>

                {/*メディアの挿入について*/}
                <Box sx={{ mb: 2 }}>
                    <DescriptionInsertMedia />
                </Box>

                <Stack
                    alignItems="center"
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{
                        flexWrap: { mb: "wrap", tb: "nowrap" }
                    }}
                >
                    <Box
                        order={1}
                    >
                        <Typography variant='body2'>
                            ファイル数：{mediaList.length.toString()}
                        </Typography>
                    </Box>
                    <Box
                        order={{ mb: 3, tb: 2 }}
                        sx={{
                            width: { mb: "100%", tb: "auto" }
                        }}
                    >
                        <Pagination
                            count={pageCount}
                            page={page}
                            onChange={handleChangePagination}
                            sx={{
                                '& .MuiPagination-ul': {
                                    justifyContent: "center"
                                }
                            }}
                        />
                    </Box>
                    <Box
                        order={{ mb: 2, tb: 3 }}
                    >
                        <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                            <InputLabel id="select-helper-label">並び替え</InputLabel>
                            <Select
                                labelId="select-helper-label"
                                id="select-helper"
                                value={sort}
                                label="並び替え"
                                onChange={(event) => handleChangeSort(event, mediaList)}
                            >
                                <MenuItem value="">
                                    <em>未選択</em>
                                </MenuItem>
                                <MenuItem value={"date-desc"}>アップロード日（新しい順）</MenuItem>
                                <MenuItem value={"date-asc"}>アップロード日（古い順）</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Stack>
            </Box>
            {/* ファイル一覧 */ }
            {mediaList &&
                <List>
                    {displayedMediaList.map((media) => (
                        <ListItem key={media.id}>
                            <MediaCard
                                id={media.id}
                                url={media.url}
                                name={media.name}
                                size={convertBytesToKB(media.size).toString() + "KB"}
                                uploadedDate={media.uploadedDate}
                                uploading={false}
                                handleDeleteMedia={handleDeleteMedia}
                            />
                            {/*setTargetEditCommentContent={setTargetEditCommentContent}*/}
                        </ListItem>
                    ))}
                </List>
            }
            {/* ページネーション */ }
            <Box
                sx={{
                    maxWidth: 980,
                    mx: "auto",
                    pt: 2,
                    pb: 10,
                }}
            >
                <Pagination
                    count={pageCount}
                    page={page}
                    onChange={handleChangePagination}
                    sx={{
                        mx: "auto",
                        '& .MuiPagination-ul': {
                            justifyContent: "center"
                        }
                    }}
                />
            </Box>
        </>
    )
}